import { getApiUrl } from '../config/api';
import { getMmecAuthHeaderFields } from '../utils/authUtils';

export interface PairDesktopRequest {
  code: string;
  sessionKey: string;
  sessionId: string;
}

export interface PairDesktopResponse {
  ok: boolean;
}

function generate6DigitCode(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function generateSessionId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function generateSessionKey(): string {
  const bytes = new Uint8Array(32);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 32; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join(''));
}

export function generatePairingCredentials(): PairDesktopRequest {
  return {
    code: generate6DigitCode(),
    sessionKey: generateSessionKey(),
    sessionId: generateSessionId(),
  };
}

/**
 * Post a pairing intent to the desktop API.
 * The browser sends to the same-origin HTTPS endpoint (/api/desktop/pair)
 * which nginx on the main backend (146) proxies to the remote flow
 * backend (local :5001 on unified 212 host). This avoids HTTPS→HTTP mixed-content blocks.
 *
 * The session token goes with it. The pairing is what gives the desktop tunnel its
 * authority, so the backend will only create one for a signed-in subscriber, and the
 * resulting tunnel acts as that user rather than as nobody.
 */
export async function postPairingIntent(payload: PairDesktopRequest): Promise<PairDesktopResponse> {
  const response = await fetch(getApiUrl('/api/desktop/pair'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getMmecAuthHeaderFields() },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`PAIR_POST_FAILED ${response.status}: ${text.slice(0, 200)}`);
  }

  return response.json();
}
